import type { BalanceOverrides, DerivedStats } from './types';
import { BALANCE } from '../data/balance';
import { DEFAULT_BALANCE, resolveBalance, type ResolvedBalance } from './balance';
import { slotSpellsFor } from '../data/slotSpells';
import {
  containerCapacity,
  containerD12Count,
  containerD20Count,
  containerD6Count,
  containerDice,
} from './container';
import type { NodeLevels } from './skilltree';
import { nodeLevel } from './skilltree';

/**
 * Calcule les statistiques dérivées à partir des niveaux de l'arbre de talents.
 * Fonction pure : aucune dépendance à l'état de la run.
 *
 * `balance` accepte soit des surcharges de debug brutes (`BalanceOverrides`),
 * soit un réglage déjà résolu (`ResolvedBalance`) — MVP_DESIGN §2 / DECISIONS D.8.
 *
 * Depuis la v0.5.0 (COMMENTS v0.4.1 §1.2) l'arbre de prestige a disparu : le
 * **contenant** vient des nœuds `ars_goblet` / `ars_hex` du même arbre, et
 * l'ensemble est conservé d'une run à l'autre.
 */
export function getDerivedStats(
  levels: NodeLevels,
  balance: ResolvedBalance | BalanceOverrides = DEFAULT_BALANCE,
): DerivedStats {
  // `resolveBalance` est idempotent : un `ResolvedBalance` se repasse tel quel.
  const bal = resolveBalance(balance);

  const dmg = nodeLevel(levels, 'ars_dmg');
  const crit = nodeLevel(levels, 'ars_crit');
  const critDmg = nodeLevel(levels, 'ars_critdmg');
  const heavy = nodeLevel(levels, 'ars_heavy');
  const fast = nodeLevel(levels, 'ars_fast');
  const gold = nodeLevel(levels, 'shop_gold');
  const goldDouble = nodeLevel(levels, 'shop_double');
  const cheap = nodeLevel(levels, 'shop_cheap');
  const slot2 = nodeLevel(levels, 'arc_slot2');
  const slot3 = nodeLevel(levels, 'arc_slot3');
  const slot4 = nodeLevel(levels, 'arc_slot4');
  const slot5 = nodeLevel(levels, 'arc_slot5');
  const power = nodeLevel(levels, 'arc_power');
  const prep = nodeLevel(levels, 'arc_prep');
  const day = nodeLevel(levels, 'arc_day');
  // v0.7.0 — COMMENTS v0.6.0 §1.1.
  const scope = nodeLevel(levels, 'ars_scope');
  const magicCrit = nodeLevel(levels, 'ars_magiccrit');
  const critMachine = nodeLevel(levels, 'ars_critmachine');
  const larcin = nodeLevel(levels, 'shop_larcin');
  const orsplosion = nodeLevel(levels, 'shop_orsplosion');
  const twin = nodeLevel(levels, 'shop_twin');
  const tinker = nodeLevel(levels, 'shop_tinker');
  const sorcery = nodeLevel(levels, 'arc_sorcery');
  const minion = nodeLevel(levels, 'arc_minion');
  const whip = nodeLevel(levels, 'arc_whip');
  // v0.26.0 — COMMENTS v0.25.0 §1.2 / A72.
  const critStreak = nodeLevel(levels, 'ars_critstreak');
  const loyalty = nodeLevel(levels, 'shop_loyalty');
  const rigged = nodeLevel(levels, 'shop_rigged');
  const slavery = nodeLevel(levels, 'arc_slavery');
  const slot6 = nodeLevel(levels, 'arc_slot6');
  const slot7 = nodeLevel(levels, 'arc_slot7');
  const flux = nodeLevel(levels, 'arc_flux');
  // v0.43.0 — A89-6 (*Avant-garde*, A89-5, retirée en v0.45, A93).
  const rearguard = nodeLevel(levels, 'arc_rearguard');
  // v0.46.0 — Abîme (A94) : *Marteau Trempé* et *Sans fond*.
  const hammer = nodeLevel(levels, 'aby_hammer');
  const bottomless = nodeLevel(levels, 'aby_bottomless');
  // v0.47.0 — les deux Grimoires (A94-11).
  const grimoireNumbers = nodeLevel(levels, 'aby_grimoire_numbers');
  const grimoireFaces = nodeLevel(levels, 'aby_grimoire_faces');

  /**
   * 1 à 7 emplacements — *Mieux vaut être plusieurs*, *Jamais deux sans trois*,
   * puis *Quatuor* et *PentaDice* (COMMENTS v0.6.0 §1.1), *Hexadice* et
   * *Heptadice* (A72-13).
   */
  const slots = 1 + slot2 + slot3 + slot4 + slot5 + slot6 + slot7;
  const expeditionDuration = bal.expeditionDuration + BALANCE.nodeDayLengthPerLevel * day;
  const container = containerDice(levels);
  /** *Bras rapide* : −5 % de temps de retour par niveau, plancher 0,5 s. */
  const throwCooldown = Math.max(
    BALANCE.throwCooldownFloor,
    bal.throwCooldown * Math.max(0, 1 - BALANCE.nodeCooldownPerLevel * fast),
  );

  return {
    /**
     * `M_T` — *Main lestée* : « ×1,05, +0,05 par niveau », donc
     * `1 + 0,05 × niveau` (COMMENTS v0.4.1 §1.2, lecture linéaire A2).
     */
    damageMult:
      (1 + BALANCE.nodeDamageMultPerLevel * heavy) *
      // *Sans fond* (A94-12) : ×(1 + 0,05 × niveau) — ×1 exact sans le nœud.
      (1 + BALANCE.bottomlessDamagePerLevel * bottomless) *
      bal.damageMult,
    /** *Lancer précis* (+2 %/niv) et *Dé à lunette* (+2 %/niv) s'additionnent. */
    critChance:
      BALANCE.baseCritChance +
      BALANCE.nodeCritPerLevel * crit +
      BALANCE.nodeScopeCritPerLevel * scope +
      bal.critChance,
    critMult: BALANCE.baseCritMultiplier + BALANCE.nodeCritDamagePerLevel * critDmg + bal.critMult,
    /** *Critique Magique* : facteur **multiplicatif** du critique (`m_C`). */
    critMultFactor: 1 + BALANCE.nodeMagicCritPerLevel * magicCrit,
    /** *Machine Critique* : temps de retour ×(1 − 0,05 × niveau) pendant 5 s. */
    critCooldownFactor: Math.max(0, 1 - BALANCE.nodeCritMachinePerLevel * critMachine),
    throwCooldown,
    /** Dés Basiques du lancer = contenu du contenant (*Gobelet*), chacun lancé avec son propre nombre de faces. */
    basicDiceCount: container.length,
    slots,
    expeditionDuration,
    /**
     * Invasion = 2 × la durée **de base** d'Expédition + le bonus de *Journée
     * longue* une seule fois (COMMENTS v0.50.0 §1.1, A101).
     */
    invasionDuration: bal.expeditionDuration * bal.invasionDurationFactor + BALANCE.nodeDayLengthPerLevel * day,
    /** *Butin* (+10 %/niv) et *Larcin* (+50 %/niv) sont **additifs** (§D.18). */
    goldMult: 1 + BALANCE.nodeGoldPerLevel * gold + BALANCE.nodeLarcinGoldPerLevel * larcin,
    /**
     * *Puissance des sorts* × *Sorcellerie* : les deux nœuds sont
     * **multiplicatifs** entre eux (A21) — jusqu'à ×2,25.
     */
    spellPower:
      (1 + BALANCE.nodeSpellPowerPerLevel * power) *
      (1 + BALANCE.nodeSorceryPerLevel * sorcery) *
      bal.spellPower,
    /**
     * Sorts d'emplacement débloqués par le nombre d'emplacements (§1.4) ;
     * v0.47.0 : les cinq sorts de *Poker* avec `aby_poker` (A94-10).
     */
    unlockedSpells: slotSpellsFor(slots, levels),
    /**
     * *Mort dorée* (1 %/niv) + *Orsplosion* (2 %/niv) : **additifs**, un seul
     * tirage à la mort de l'ennemi (§D.18).
     */
    goldDoubleChance:
      BALANCE.nodeGoldDoublePerLevel * goldDouble + BALANCE.nodeOrsplosionPerLevel * orsplosion,
    /** *Gagnant-Gagnant* : 1 %/niveau d'un second dé par dé tiré. */
    twinDieChance: BALANCE.nodeTwinChancePerLevel * twin,
    /** *Larbin* débloqué par `arc_minion`. */
    minionUnlocked: minion >= 1,
    /** *Larbin* : temps de retour ×1,25, réduit de 5 %/niveau par *Coup de fouet*. */
    minionCooldown:
      throwCooldown *
      BALANCE.minionCooldownFactor *
      Math.max(0, 1 - BALANCE.nodeWhipCooldownPerLevel * whip),
    /**
     * *Préparation* (v0.50.0, A100-2) : renfort des sorts de dé des dés des
     * emplacements 1 à 3, `0,1 × niveau` (le compteur a disparu).
     */
    prepBoost: BALANCE.nodePrepPerLevel * prep,
    /** Nœud *Petit lancer* : +1 dégât plat par dé et par niveau. */
    flatDamagePerDie: BALANCE.nodeFlatDamagePerLevel * dmg + bal.flatDamagePerDie,
    /** Plus aucun nœud n'accorde de chance de face maximale (COMMENTS §3). */
    masterWeightedChance: 0,
    /**
     * Multiplicateur de poids des raretés > Commun : *Modification mécanique*
     * et *Bidouilleur* ont le **même** effet et se cumulent (§D.18).
     */
    rarityWeightMult:
      1 + BALANCE.nodeRarityPerLevel * (nodeLevel(levels, 'shop_rarity') + tinker + rigged),
    /** *Négociateur* + *Carte de fidélité* : niveaux de remise **additifs** (A72-10). */
    gachaDiscountLevel: cheap + loyalty,
    /** *Dice Master* : moyenne des raretés des Maîtres équipés (A72-5). */
    diceMaster: nodeLevel(levels, 'ars_dicemaster') >= 1,
    /** *Criticité* : `1 + 0,05 × niveau` par critique consécutif (A72-3). */
    critStreakFactor: 1 + BALANCE.nodeCritStreakPerLevel * critStreak,
    /** *Avantage critique* : deux tirages de critique (A72-7). */
    critAdvantage: nodeLevel(levels, 'ars_critadv') >= 1,
    /** *Triche* : deux lancers, le meilleur total est gardé (A72-6). */
    cheat: nodeLevel(levels, 'ars_cheat') >= 1,
    /** *Flux arcanique* : `1 + 0,05 × niveau` par lancer consécutif à sort (A72-3). */
    spellStreakFactor: 1 + BALANCE.nodeSpellStreakPerLevel * flux,
    /** *Arrière-garde* : renfort des sorts des deux derniers dés, `0,04 × niveau` (A89-6). */
    rearguardBoost: BALANCE.nodeRearguardPerLevel * rearguard,
    /** Plafond de niveau des dés : 20, +2 par niveau de *Marteau Trempé* (A94-5). */
    dieLevelMax: BALANCE.dieLevelMax + BALANCE.hammerDieLevelPerLevel * hammer,
    /** Remise de la Forge : −5 % par niveau de *Marteau Trempé* (−50 % au niveau 10). */
    forgeDiscount: Math.min(1, BALANCE.hammerForgeDiscountPerLevel * hammer),
    /** *Grimoire des nombres* : niveau 0 à 5 (A94-11). */
    grimoireNumbers,
    /** *Face je gagne, Pile tu perds* : niveau 0 à 5 (A94-11). */
    grimoireFaces,
    /** *Larbin* : ×0,75, +0,1 par niveau d'*Esclavagisme* (A72-12). */
    minionDamageFactor: BALANCE.minionDamageFactor + BALANCE.nodeSlaveryPerLevel * slavery,
    /** Nombre de dés du contenant (0 sans *Gobelet*) — toujours plein. */
    containerCapacity: containerCapacity(levels),
    /** Idem, sous son nom explicite : dés Basiques lancés à chaque lancer. */
    containerDiceCount: container.length,
    /** Dés d6 parmi eux (*Contenant hexagonal*). */
    containerD6Count: containerD6Count(levels),
    /** Dés d12 parmi eux (*Dodécaédrique*) ; le reste est en d4. */
    containerD12Count: containerD12Count(levels),
    /** Dés d20 parmi eux (*Icosaèdrique*), servis en premier. */
    containerD20Count: containerD20Count(levels),
    // A111 : facteurs des dés à niveau et Corrupted, surchargeables en debug.
    dieLevelFactor: bal.dieLevelFactor,
    corruptedFlatFactor: bal.corruptedFlatFactor,
  };
}
